/**
 * NEO: 선택형 추천상품(안 1) 장바구니 추가 기능
 *
 * [이 파일의 역할]
 * 추천상품 토글 중 고객이 선택한 상품을 찾아 Shopify AJAX Cart API로 추가한다.
 * 이 파일이 메인 상품을 직접 추가하는 것은 아니다. 메인 상품과 OPTIS/BSS 옵션은
 * `neo-optis-product-form.js`가 연결한 BSS 흐름에서 먼저 처리된다. 메인 상품 추가가
 * 성공한 뒤 `window.neo_addSelectedUpsells()`가 호출되며, 선택된 추천상품 처리가
 * 끝나면 호출 측에서 Cart Drawer를 새로고침한다.
 *
 * [전체 처리 순서]
 * 1. 메인 Add to Cart 클릭
 * 2. OPTIS/BSS가 메인 상품과 선택 옵션을 장바구니에 추가
 * 3. 이 파일의 `neo_addSelectedUpsells()` 실행
 * 4. 켜져 있는 추천상품 토글의 Variant ID 수집
 * 5. 추천상품별로 `/cart/add.js` 요청을 보내 각 상품의 성공/실패를 구분
 * 6. 호출 측인 `neo-optis-product-form.js`가 Cart Drawer 갱신
 *
 * [Liquid/CSS 의존성]
 * - `upsell-product.liquid`에서 토글 input에 `data-neo-upsell-select`와 Variant ID를
 *   출력해야 한다.
 * - 오류를 표시할 요소에는 `data-neo-upsell-errors`가 있어야 한다.
 * - 토글 표시와 오류 노출 스타일은 `neo-product-upsells.css`가 담당한다.
 *
 * [운영상 주의사항]
 * 메인 상품과 추천상품은 서로 다른 요청으로 처리된다. 따라서 추천상품 요청이
 * 실패하더라도 앞서 추가된 메인 상품은 장바구니에 남을 수 있다. 이 파일을 수정할
 * 때에는 '모두 한 번에 성공하거나 모두 취소되는 단일 주문 요청'이 아니라는 점을
 * 반드시 고려해야 한다.
 */
(function neo_installSelectedUpsells() {
  // Shopify 테마 편집기의 섹션 재로딩 등으로 이 asset이 다시 실행될 수 있다.
  // 이미 전역 함수를 설치했다면 중복 등록하지 않고 종료한다.
  if (window.neo_selectedUpsellsInstalled) return;

  // 체크되어 있고 활성화된 추천상품 input만 수집한다.
  // :checked는 고객이 켠 토글만, :not(:disabled)는 구매 불가능하게 비활성화된
  // input을 제외하기 위한 조건이다.
  const neo_selectedUpsellSelector =
    '[data-neo-upsell-select]:checked:not(:disabled)';

  // OPTIS 메인 상품 처리가 끝난 후 호출할 수 있도록 전역 함수로 공개한다.
  // async 함수이므로 호출 측에서는 이 Promise가 끝난 다음 Cart Drawer를 갱신한다.
  window.neo_addSelectedUpsells = async function neo_addSelectedUpsells() {
    // querySelectorAll의 NodeList를 Array로 변환해 map 등 배열 메서드를 사용한다.
    const neo_selectedInputs = Array.from(
      document.querySelectorAll(neo_selectedUpsellSelector)
    );

    // 선택된 추천상품이 없으면 별도의 네트워크 요청을 만들지 않는다.
    if (neo_selectedInputs.length === 0) return null;

    // 이전 장바구니 추가 시도에서 남은 오류가 현재 결과와 섞이지 않도록
    // 모든 선택형 추천상품의 오류 영역을 먼저 초기화한다.
    document.querySelectorAll('[data-neo-upsell-errors]').forEach((neo_container) => {
      neo_container.textContent = '';
      neo_container.classList.remove('is-visible');
    });

    // 각 input과 Shopify에 보낼 item, 해당 상품 전용 오류 영역을 하나로 묶는다.
    // 오류 영역을 input에서 가장 가까운 product-upsell__holder 안에서만 찾으므로
    // A 상품의 재고 오류가 B 상품 카드에도 표시되는 문제를 방지한다.
    const neo_entries = neo_selectedInputs.map((neo_input) => {
      const neo_item = {
        // input value는 문자열이므로 Shopify가 요구하는 숫자 Variant ID로 변환한다.
        id: Number(neo_input.value),
        // 현재 기획에서는 토글 하나당 추천상품 1개를 추가한다.
        quantity: 1,
      };

      // 예약상품일 경우 Liquid에서 data 속성으로 전달한 line item property의
      // 이름과 값을 읽는다. 일반 상품에는 두 data 속성이 존재하지 않는다.
      const neo_propertyName = neo_input.dataset.neoPropertyName;
      const neo_propertyValue = neo_input.dataset.neoPropertyValue;

      // 기존 Quick Buy와 동일하게 예약상품의 line item property를 보존한다.
      if (neo_propertyName && neo_propertyValue) {
        neo_item.properties = {
          [neo_propertyName]: neo_propertyValue,
        };
      }

      const neo_holder = neo_input.closest('[data-upsell-holder]');
      const neo_errorContainer = neo_holder?.querySelector('[data-neo-upsell-errors]');

      return {
        input: neo_input,
        item: neo_item,
        errorContainer: neo_errorContainer,
      };
    });

    // Shopify Markets 또는 하위 경로 언어 URL에서도 올바르게 동작하도록
    // Shopify가 제공하는 routes.root를 사용한다. 값이 없을 때만 `/`를 사용한다.
    const neo_cartAddUrl = `${window.Shopify?.routes?.root || '/'}cart/add.js`;

    const neo_results = [];
    const neo_failures = [];

    // 여러 상품을 한 요청으로 보내면 Shopify의 오류 응답만으로 어느 Variant가
    // 실패했는지 확실하게 구분할 수 없다. 정확한 상품 카드에 오류를 표시하기 위해
    // 선택된 추천상품을 한 개씩 순서대로 요청한다.
    for (const neo_entry of neo_entries) {
      try {
        const neo_response = await fetch(neo_cartAddUrl, {
          method: 'POST',
          headers: {
            // Shopify에 JSON 응답과 JSON 요청 본문을 사용한다고 명시한다.
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          // 요청 하나에는 현재 처리 중인 추천상품만 포함한다.
          body: JSON.stringify({ items: [neo_entry.item] }),
        });

        // fetch는 HTTP 4xx/5xx 응답에서도 자동으로 예외를 발생시키지 않으므로
        // response.ok를 직접 확인한다. 재고 부족이나 판매 불가 Variant 등이 해당한다.
        if (!neo_response.ok) {
          // Shopify 오류 응답이 JSON이 아닐 가능성도 있으므로 파싱 실패 시 빈 객체를 쓴다.
          const neo_error = await neo_response.json().catch(() => ({}));
          const neo_message =
            neo_error.description ||
            neo_error.message ||
            '추천상품을 장바구니에 담지 못했습니다.';

          // 실패한 input과 같은 상품 카드 안의 오류 영역에만 문구를 표시한다.
          if (neo_entry.errorContainer) {
            neo_entry.errorContainer.textContent = neo_message;
            neo_entry.errorContainer.classList.add('is-visible');
          }

          throw new Error(neo_message);
        }

        // 성공 응답은 향후 분석이나 후속 처리에서 사용할 수 있도록 배열에 보존한다.
        neo_results.push(await neo_response.json());
      } catch (neo_error) {
        // 한 추천상품이 실패해도 다음 추천상품의 추가 시도는 계속한다.
        // 네트워크 오류처럼 위에서 메시지를 만들지 못한 경우에도 해당 카드에 표시한다.
        if (neo_entry.errorContainer && !neo_entry.errorContainer.textContent) {
          neo_entry.errorContainer.textContent = '추천상품을 장바구니에 담지 못했습니다.';
          neo_entry.errorContainer.classList.add('is-visible');
        }

        neo_failures.push(neo_error);
      }
    }

    // 하나라도 실패했다면 호출 측에 실패 상태를 전달한다. 호출 측의 finally에서는
    // 성공한 상품과 기존 메인 상품을 정확히 보여주기 위해 Cart Drawer를 갱신한다.
    if (neo_failures.length > 0) {
      throw neo_failures[0];
    }

    return neo_results;
  };

  // 전역 함수 등록이 모두 완료된 뒤 설치 완료 상태를 기록한다.
  window.neo_selectedUpsellsInstalled = true;
})();
